import { useState } from "react";
import { Mail, MapPin, Phone, Send, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import PageMeta from "../components/PageMeta";
import Footer from "../components/Footer";
import FloatingFAQ from "../components/FloatingFAQ";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers.length ? `(${numbers}` : '';
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)})${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="noise-bg">
      <PageMeta
        title="Contact Us | Averoan Technologies"
        description="Have a project in mind? Contact Averoan Technologies in Vancouver, BC. We reply within 24 hours."
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-8 relative" data-testid="contact-hero">
        <div className="hero-glow absolute top-0 right-0 opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl">
            <span className="tag mb-6">Contact</span>
            <h1 
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Let's <span style={{ color: '#D4FF00' }}>talk</span>
            </h1>
            <p 
              className="text-base md:text-lg max-w-lg"
              style={{ color: '#A1A1AA' }}
            >
              Have a project in mind? Fill out the form and we'll get back to you within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 md:py-20" style={{ background: '#18181B' }} data-testid="contact-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column: Contact Info */}
            <div>
              <h2 
                className="text-2xl font-bold mb-8 normal-case"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                Get in touch
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}
                  >
                    <Mail size={16} style={{ color: '#D4FF00' }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm mb-1">Email</p>
                    <a href="mailto:info@averoan.com" className="text-sm hover:text-[#D4FF00] transition-colors" style={{ color: '#A1A1AA' }}>info@averoan.com</a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}
                  >
                    <Phone size={16} style={{ color: '#D4FF00' }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm mb-1">Phone</p>
                    <a href="tel:+17787887273" className="text-sm hover:text-[#D4FF00] transition-colors" style={{ color: '#A1A1AA' }}>(778) 788-7273</a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}
                  >
                    <MapPin size={16} style={{ color: '#D4FF00' }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm mb-1">Location</p>
                    <p className="text-sm" style={{ color: '#A1A1AA' }}>Vancouver, BC</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-xl" style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}
                  >
                    <Clock size={16} style={{ color: '#D4FF00' }} />
                  </div>
                  <div>
                    <p className="font-medium text-sm mb-1">Response Time</p>
                    <p className="text-sm" style={{ color: '#A1A1AA' }}>Within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* CTA Card */}
              <div 
                className="p-6 rounded-xl"
                style={{ background: 'rgba(212, 255, 0, 0.05)', border: '1px solid rgba(212, 255, 0, 0.2)' }}
              >
                <h3 className="font-bold mb-2" style={{ fontFamily: '"Clash Display", sans-serif' }}>
                  Need a detailed quote?
                </h3>
                <p className="text-sm mb-4" style={{ color: '#A1A1AA' }}>
                  Fill out our consultation form for a custom proposal tailored to your project.
                </p>
                <Link 
                  to="/consultation"
                  className="btn-primary text-sm"
                  data-testid="consultation-link"
                >
                  Get Started
                </Link>
              </div>
            </div>

            {/* Right Column: Form */}
            <div>
              <h2 
                className="text-2xl font-bold mb-8 normal-case"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                Send us a message
              </h2>
              
              <form 
                onSubmit={handleSubmit} 
                className="space-y-6 p-6 rounded-xl"
                style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.05)' }}
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Your full name"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="you@company.com"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="input-field"
                      placeholder="(000)000-0000"
                      maxLength={13}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>
                      Service Interested In
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="input-field cursor-pointer"
                      style={{ background: 'transparent' }}
                      required
                      data-testid="contact-service-select"
                    >
                      <option value="" disabled style={{ background: '#18181B' }}>Select a service</option>
                      <option value="website" style={{ background: '#18181B' }}>Website Design & Development</option>
                      <option value="software" style={{ background: '#18181B' }}>Custom Software</option>
                      <option value="ai" style={{ background: '#18181B' }}>AI Automation</option>
                      <option value="seo" style={{ background: '#18181B' }}>SEO & Marketing</option>
                      <option value="other" style={{ background: '#18181B' }}>Other</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs uppercase tracking-wider mb-3" style={{ color: '#D4FF00' }}>
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="input-field min-h-[120px] resize-none"
                    placeholder="Tell us about your project..."
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                

                {/* Not for people. Hidden, unlabelled, skipped by the tab key
                    and excluded from autofill - a bot fills every input it
                    finds in the HTML, and filling this one is the giveaway.
                    The server bins anything that arrives with it set. */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                  data-testid="spam-trap"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? 'Sending...' : <>Send Message <Send size={16} /></>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
